import React from "react";
import {
  Box,
  HStack,
  SimpleGrid,
  Skeleton,
  useColorModeValue,
} from "@chakra-ui/react";

export default function AnalyticsSkeleton({ heatmapColumns = { base: 2, sm: 3, md: 4, lg: 6 }, heatmapCount = 8 }) {
  const cardBg = useColorModeValue("white", "gray.700");
  const headerBg = useColorModeValue("gray.200", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const shimmerStart = useColorModeValue("gray.100", "gray.700");
  const shimmerEnd = useColorModeValue("blue.50", "gray.600");
  const chartStart = useColorModeValue("green.50", "green.900");
  const chartEnd = useColorModeValue("blue.50", "blue.900");

  const skeletonProps = { startColor: shimmerStart, endColor: shimmerEnd, speed: 1.2 };
  const chartSkeletonProps = { startColor: chartStart, endColor: chartEnd, speed: 1.2 };

  return (
    <>
      {/* Page Heading */}
      <Skeleton height="16px" width="180px" {...skeletonProps} />

      {/* Section Heading */}
      <Skeleton height="14px" width="160px" mb={-1} {...skeletonProps} />

      {/* Heatmap Cards */}
      <SimpleGrid columns={heatmapColumns} spacing={3}>
        {Array.from({ length: heatmapCount }).map((_, i) => (
          <Box key={i} p={3} rounded="md" bg={i % 2 === 0 ? "green.400" : "red.400"} opacity={0.4}>
            <Skeleton height="14px" width="60px" mx="auto" mb={2} {...chartSkeletonProps} />
            <Skeleton height="10px" width="70px" mx="auto" mb={1} {...chartSkeletonProps} />
            <Skeleton height="10px" width="50px" mx="auto" mb={1} {...chartSkeletonProps} />
            <Skeleton height="10px" width="45px" mx="auto" {...chartSkeletonProps} />
          </Box>
        ))}
      </SimpleGrid>

      {/* Bar Chart */}
      <Box bg={cardBg} rounded="lg" boxShadow="sm" p={4} h="400px" display="flex" flexDirection="column">
        <Skeleton height="14px" width="140px" mb={4} {...skeletonProps} />
        <Box flex="1" display="flex" alignItems="flex-end" gap={2}>
          {[65, 40, 80, 55, 90, 45, 70, 35, 60, 75, 50, 85].map((h, i) => (
            <Skeleton
              key={i}
              flex="1"
              height={`${h}%`}
              borderRadius="2px"
              {...chartSkeletonProps}
            />
          ))}
        </Box>
      </Box>

      {/* Table Section Heading */}
      <Skeleton height="14px" width="100px" {...skeletonProps} />

      {/* Table */}
      <Box bg={cardBg} rounded="lg" boxShadow="sm" overflow="hidden">
        {/* Table Header */}
        <HStack bg={headerBg} px={3} py={2} spacing={4}>
          {[60, 55, 45, 50, 60, 70, 55].map((w, i) => (
            <Skeleton key={i} height="12px" width={`${w}px`} flexShrink={0} {...skeletonProps} />
          ))}
        </HStack>
        {/* Table Rows */}
        {Array.from({ length: 6 }).map((_, row) => (
          <HStack key={row} px={3} py={3} spacing={4} borderTopWidth="1px" borderColor={borderColor}>
            {[50, 40, 35, 40, 50, 60, 45].map((w, col) => (
              <Skeleton key={col} height="12px" width={`${w + (row % 3) * 5}px`} flexShrink={0} {...skeletonProps} />
            ))}
          </HStack>
        ))}
      </Box>
    </>
  );
}
